import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { usePromotionById } from '../../hooks/queries';
import { formatPromotionUses } from '../../utils/promotion-uses';
import {
  formatScheduleDate,
  getMeaningfulDisplayName,
  getPromotionTypeLabel,
} from '../../utils/promotion-display';
import {
  collectPromotionTargetingGroups,
  isCustomerGroupTargetingField,
} from '../../utils/promotion-targeting-groups';
import { PromotionRuleProducts } from './PromotionRuleProducts';
import { PromotionTargetingGroups } from './PromotionTargetingGroups';

const cardStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  border: '1px solid #ddd',
  borderRadius: '8px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  width: '100%',
  boxSizing: 'border-box',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: '0 0 1rem 0',
  fontSize: '1.25rem',
  fontWeight: 600,
};

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontWeight: 600,
  marginBottom: '0.35rem',
  color: '#333',
};

const fieldValueStyle: React.CSSProperties = {
  margin: 0,
  padding: '0.65rem 0.75rem',
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  backgroundColor: '#fafafa',
  color: '#333',
};

function isPromotionActive(promo: Record<string, unknown>): boolean {
  if (promo.status !== undefined && promo.status !== null) {
    const status = String(promo.status).toLowerCase().trim();
    return status === 'enabled' || status === 'active';
  }
  if (promo.enabled !== undefined && promo.enabled !== null) {
    return promo.enabled === true;
  }
  return false;
}

export function PromotionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const promotionType = searchParams.get('type') ?? undefined;
  const searched = searchParams.get('searched') === '1';

  const { data: response, isLoading: loading, error: queryError } = usePromotionById(
    id ? parseInt(id) : 0,
    promotionType,
    searched,
  );
  const promotion = (response?.data ?? null) as Record<string, unknown> | null;
  const error = queryError
    ? String((queryError as { message?: string }).message ?? queryError)
    : response?.success === false
      ? String(response.message ?? 'Failed to load promotion')
      : null;

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-state">
          <div className="spinner" />
          Loading promotion details…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="error-banner">⚠ {error}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/promotions')}>
          Back to Promotions
        </button>
      </div>
    );
  }

  if (!promotion) {
    return null;
  }

  const rulesDisplay = Array.isArray(promotion.rules_display)
    ? (promotion.rules_display as Array<{
        condition: string;
        reward: string;
        products?: Array<{
          productId: number;
          variantId?: number;
          name: string;
          sku?: string;
        }>;
      }>)
    : [];
  const targetingDisplay = Array.isArray(promotion.targeting_display)
    ? (promotion.targeting_display as Array<{ field: string; operator: string; value: string }>)
    : [];
  const targetingCustomerGroups = collectPromotionTargetingGroups(promotion, targetingDisplay);
  const otherTargetingDisplay = targetingDisplay.filter(
    (row) => !isCustomerGroupTargetingField(row.field),
  );
  const usageLimits = promotion.usage_limits_display as
    | { totalUsesLimit?: string; canUseWithOtherPromotions?: boolean }
    | undefined;
  const rewardMode = promotion.reward_mode === 'stacked' ? 'stacked' : 'tiered';
  const startDate = (promotion.start_date || promotion.date_start || promotion.starts_at) as
    | string
    | undefined;
  const endDate = (promotion.end_date || promotion.date_end || promotion.expires_at) as
    | string
    | undefined;
  const displayName = getMeaningfulDisplayName(promotion.display_name);
  const titleMeta = [
    getPromotionTypeLabel(promotion),
    `Uses ${formatPromotionUses(promotion)}`,
    isPromotionActive(promotion) ? 'Active' : 'Inactive',
  ]
    .map((part) => `| ${part}`)
    .join(' ');

  return (
    <div
      className="page-container promotion-detail-page"
      style={{ maxWidth: '1680px', width: '100%', boxSizing: 'border-box' }}
    >
      <div className="page-header">
        <div>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/promotions')}>
            ← Back
          </button>
          <h1 className="page-title promotion-detail-title" style={{ marginTop: '1rem' }}>
            <span>{String(promotion.name || `Promotion #${promotion.id}`)}</span>
            <span className="promotion-detail-title-meta">{titleMeta}</span>
          </h1>
        </div>
      </div>

      {displayName && (
        <div style={cardStyle}>
          <h2 style={sectionTitleStyle}>Summary</h2>
          <div style={{ display: 'grid', gap: '1rem' }}>
            <div>
              <span style={fieldLabelStyle}>Display name</span>
              <p style={fieldValueStyle}>{displayName}</p>
            </div>
          </div>
        </div>
      )}

      {rulesDisplay.length > 0 && (
        <div className="promotion-rules-card" style={cardStyle}>
          <h2 style={sectionTitleStyle}>Rules</h2>
          <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
            What conditions must a customer satisfy to receive the reward you&apos;re offering?
          </p>
          <div
            className={
              rulesDisplay.length > 6
                ? 'promotion-rules-table-wrap scrollable'
                : 'promotion-rules-table-wrap'
            }
          >
            <table className="data-table promotion-rules-table">
              <thead>
                <tr>
                  <th>Condition</th>
                  <th>Reward</th>
                  <th>Products</th>
                </tr>
              </thead>
              <tbody>
                {rulesDisplay.map((rule, index) => (
                  <tr key={`${rule.condition}-${index}`}>
                    <td>{rule.condition}</td>
                    <td>{rule.reward}</td>
                    <td>
                      <PromotionRuleProducts products={rule.products ?? []} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rulesDisplay.length > 1 && (
            <div style={{ marginTop: '1.25rem' }}>
              <span style={fieldLabelStyle}>How many of the above rewards can customers have?</span>
              <div style={{ marginTop: '0.5rem', display: 'grid', gap: '0.35rem' }}>
                <label className="promotion-reward-mode-option">
                  <input type="radio" checked={rewardMode === 'tiered'} readOnly />
                  <strong>Tiered reward</strong>
                  <span>
                    — Give the last reward customer qualifies for (&quot;get 10% off&quot; OR
                    &quot;get 15% off&quot;)
                  </span>
                </label>
                <label className="promotion-reward-mode-option">
                  <input type="radio" checked={rewardMode === 'stacked'} readOnly />
                  <strong>Stacked rewards</strong>
                  <span>
                    — Give all rewards customer qualifies for (&quot;get 10% off&quot; AND
                    &quot;free shipping&quot;)
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      {(otherTargetingDisplay.length > 0 || targetingCustomerGroups.length > 0) && (
        <div style={cardStyle}>
          <h2 style={sectionTitleStyle}>Targeting</h2>
          <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
            Target customers if
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {otherTargetingDisplay.map((row, index) => (
              <div
                key={`${row.field}-${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#f9a825',
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontWeight: 500 }}>{row.field}</span>
                {row.operator ? <span>{row.operator}</span> : null}
                <span
                  style={{
                    padding: '0.35rem 0.65rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                  }}
                >
                  {row.value}
                </span>
              </div>
            ))}
            <PromotionTargetingGroups groups={targetingCustomerGroups} />
          </div>
        </div>
      )}

      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>Schedule</h2>
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div>
            <span style={fieldLabelStyle}>Promotion starts</span>
            <p style={fieldValueStyle}>{formatScheduleDate(startDate)}</p>
          </div>
          <div>
            <span style={fieldLabelStyle}>Promotion ends</span>
            <p style={fieldValueStyle}>{formatScheduleDate(endDate)}</p>
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>Usage limits</h2>
        <div style={{ display: 'grid', gap: '1.25rem' }}>
          <div>
            <span style={fieldLabelStyle}>Promotion level — How many times this promotion can be applied</span>
            <div style={{ marginTop: '0.5rem', display: 'grid', gap: '0.5rem' }}>
              <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="radio"
                  checked={(usageLimits?.totalUsesLimit ?? 'No limit') === 'No limit'}
                  readOnly
                />
                <span>No limit</span>
              </label>
              <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="radio"
                  checked={(usageLimits?.totalUsesLimit ?? 'No limit') !== 'No limit'}
                  readOnly
                />
                <span>
                  Limit total uses: {usageLimits?.totalUsesLimit ?? formatPromotionUses(promotion)}
                </span>
              </label>
            </div>
          </div>

          <div>
            <span style={fieldLabelStyle}>Allow usage with other promotions</span>
            <p style={{ ...fieldValueStyle, marginTop: '0.5rem' }}>
              This promotion{' '}
              {usageLimits?.canUseWithOtherPromotions !== false ? 'can' : 'cannot'} be used in
              conjunction with other promotions
            </p>
          </div>

          <div>
            <span style={fieldLabelStyle}>Current uses</span>
            <p style={{ ...fieldValueStyle, marginTop: '0.5rem' }}>{formatPromotionUses(promotion)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
